import Box from '@material-ui/core/Box';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { CostComparison, UnresolvedPrice } from './types';

/** One formatter, so every number on the page agrees about what a euro is. */
const EUR = new Intl.NumberFormat('en-IE', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function formatEur(value: number): string {
  return EUR.format(value);
}

/** An absent side of a change is a dash, never a zero — they mean different things. */
export function formatEurOrDash(value: number | null): string {
  return value === null ? '—' : formatEur(value);
}

export function formatDifference(value: number): string {
  if (value === 0) return formatEur(0);
  return `${value > 0 ? '+' : '-'}${formatEur(Math.abs(value))}`;
}

/** Up is bad, down is good, nothing is neither. */
export function differenceColor(value: number): string | undefined {
  if (value > 0) return '#c62828';
  if (value < 0) return '#2e7d32';
  return undefined;
}

export const CostTotal = (props: {
  total: number;
  partial: boolean;
  generatedAt?: string;
}) => (
  <Box mb={2}>
    <div style={{ fontSize: 28, fontWeight: 600 }}>
      {formatEur(props.total)}
      <span style={{ fontSize: 16, fontWeight: 400 }}> / month</span>
    </div>
    <div style={{ fontSize: 12, opacity: 0.75 }}>
      {props.partial
        ? 'Partial total — some prices could not be retrieved.'
        : 'Estimated fixed monthly cost across the selected environments.'}
    </div>
    {props.generatedAt && (
      <div style={{ fontSize: 12, opacity: 0.6, marginTop: 4 }}>
        Azure public retail prices in EUR, retrieved{' '}
        {new Date(props.generatedAt).toLocaleString()}. Excludes contract
        discounts, credits and tax.
      </div>
    )}
  </Box>
);

export const IncompleteEstimateBanner = (props: {
  unresolved: UnresolvedPrice[];
}) => {
  if (props.unresolved.length === 0) return null;

  return (
    <Box
      mb={2}
      p={2}
      role="status"
      style={{
        border: '1px solid #ffb74d',
        borderRadius: 4,
        background: '#fff4e5',
        color: '#663c00',
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 8 }}>Estimate incomplete</div>
      <div style={{ fontSize: 13, marginBottom: 8 }}>
        These resources are excluded from the totals because their price could
        not be retrieved. You can still submit the form.
      </div>
      <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13 }}>
        {props.unresolved.map(item => (
          <li key={item.stableKey}>
            {item.environment} · {item.displayName} — {item.reason}
          </li>
        ))}
      </ul>
    </Box>
  );
};

/** The Day-2 headline: Before, After, Different, per environment and in total. */
export const CostComparisonTable = (props: { comparison: CostComparison }) => (
  <Table size="small">
    <TableHead>
      <TableRow>
        <TableCell>Environment</TableCell>
        <TableCell align="right">Before</TableCell>
        <TableCell align="right">After</TableCell>
        <TableCell align="right">Different</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {props.comparison.environments.map(environment => (
        <TableRow key={environment.environment}>
          <TableCell>{environment.environment}</TableCell>
          <TableCell align="right">
            {formatEurOrDash(environment.before)}
          </TableCell>
          <TableCell align="right">
            {formatEurOrDash(environment.after)}
          </TableCell>
          <TableCell
            align="right"
            style={{ color: differenceColor(environment.different) }}
          >
            {formatDifference(environment.different)}
          </TableCell>
        </TableRow>
      ))}
      <TableRow>
        <TableCell>
          <strong>Total</strong>
        </TableCell>
        <TableCell align="right">
          <strong>{formatEur(props.comparison.before)}</strong>
        </TableCell>
        <TableCell align="right">
          <strong>{formatEur(props.comparison.after)}</strong>
        </TableCell>
        <TableCell
          align="right"
          style={{ color: differenceColor(props.comparison.different) }}
        >
          <strong>{formatDifference(props.comparison.different)}</strong>
        </TableCell>
      </TableRow>
    </TableBody>
  </Table>
);
